import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { getImportLog } from '../services/api/datasetService';

/**
 * ImportLogDialog - Displays detailed import log with per-person status
 *
 * Props:
 * - open: Boolean, controls dialog visibility
 * - datasetId: Dataset ID
 * - importId: Import ID
 * - onClose: Function called when dialog closes
 */
export default function ImportLogDialog({ open, datasetId, importId, onClose }) {
  const [logData, setLogData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');

  useEffect(() => {
    if (open && datasetId && importId) {
      loadImportLog();
    }
  }, [open, datasetId, importId]);

  async function loadImportLog() {
    try {
      setLoading(true);
      setError(null);
      const data = await getImportLog(datasetId, importId);
      setLogData(data);
    } catch (err) {
      console.error('Failed to load import log:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function formatDate(isoString) {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  // Filter person records by search term
  const filteredPersons = logData?.person_records?.filter(person => {
    if (!searchFilter) return true;
    const searchLower = searchFilter.toLowerCase();
    return (
      person.person_id?.toLowerCase().includes(searchLower) ||
      person.name?.toLowerCase().includes(searchLower) ||
      person.status?.toLowerCase().includes(searchLower) ||
      person.error?.toLowerCase().includes(searchLower)
    );
  }) || [];

  const successCount = logData?.person_records?.filter(p => p.status === 'SUCCESS').length || 0;
  const failedCount = logData?.person_records?.filter(p => p.status === 'FAILED').length || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6">Import Log</Typography>
          <Typography variant="caption" color="text.secondary">
            Import ID: {importId}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && logData && (
          <>
            {/* Summary Section */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Typography variant="overline" color="text.secondary">
                Import Summary
              </Typography>
              <Box sx={{ mt: 1, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Started:</Typography>
                  <Typography variant="body2">{formatDate(logData.started_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Completed:</Typography>
                  <Typography variant="body2">{formatDate(logData.completed_at)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Persons:</Typography>
                  <Typography variant="body2">{logData.summary.total_persons}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Results:</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip label={`${successCount} Success`} color="success" size="small" />
                    <Chip label={`${failedCount} Failed`} color="error" size="small" />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Created:</Typography>
                  <Typography variant="body2">{logData.summary.created}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Updated:</Typography>
                  <Typography variant="body2">{logData.summary.updated}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Faces Indexed:</Typography>
                  <Typography variant="body2">{logData.summary.faces_indexed}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Duplicates Skipped:</Typography>
                  <Typography variant="body2">{logData.summary.duplicates_skipped}</Typography>
                </Box>
              </Box>
            </Paper>

            {/* Search Filter */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search by person ID, name, status, or error..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              sx={{ mb: 2 }}
            />

            {/* Person Records Table */}
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Person Records ({filteredPersons.length} of {logData.person_records.length})
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Person ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPersons.map((person, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: person.status === 'FAILED' ? 'error.lighter' : 'inherit',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {person.person_id}
                      </TableCell>
                      <TableCell>{person.name}</TableCell>
                      <TableCell>
                        {person.status === 'SUCCESS' ? (
                          <Chip icon={<SuccessIcon />} label="Success" color="success" size="small" />
                        ) : (
                          <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {person.details?.action || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {person.status === 'SUCCESS' ? (
                          <Typography variant="caption">
                            Faces: {person.details?.faces_indexed ? 'Yes' : 'No'} |
                            Attrs: {person.details?.attributes_detected ? 'Yes' : 'No'}
                          </Typography>
                        ) : (
                          <Accordion sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                            <AccordionSummary expandIcon={<ExpandIcon />} sx={{ minHeight: 'auto', px: 1 }}>
                              <Typography variant="caption" color="error">View Error</Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 1, pt: 0 }}>
                              <Typography variant="caption" color="error" component="pre" sx={{ whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}>
                                {person.error}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
